import { Link, useNavigation } from "@remix-run/react";
import { type MetaFunction } from "@remix-run/node";
import { useState, useEffect } from "react";
import { ClipboardIcon, LinkIcon, ArrowTopRightOnSquareIcon, XCircleIcon } from '@heroicons/react/24/outline';
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/styles/quick-whats.css";

// Translations for Quick Whats App
const quickWhatsTranslations = {
  ar: {
    title: "واتس مباشر",
    description: "أنشئ روابط واتساب مباشرة بسهولة.",
    enterPhoneNumber: "أدخل رقم الهاتف:",
    countryCode: "رمز الدولة:",
    phoneNumberPlaceholder: "مثال: 501234567",
    optionalMessage: "رسالة اختيارية (سيتم تعبئتها مسبقًا):",
    messagePlaceholder: "مرحبًا! كيف يمكنني مساعدتك؟",
    generateLink: "إنشاء الرابط",
    generating: "جاري الإنشاء...",
    openInWhatsApp: "فتح في واتساب",
    copyLink: "نسخ الرابط",
    copied: "تم النسخ!",
    linkCopiedToClipboard: "تم نسخ الرابط إلى الحافظة!",
    errorInvalidPhoneNumber: "الرجاء إدخال رقم هاتف صالح.",
    errorCountryCodeMissing: "الرجاء اختيار رمز الدولة.",
    generatedLink: "الرابط المُنشأ:",
    uiLanguageToggle: "Switch to English (UI)",
    darkModeToggle: "تفعيل الوضع المظلم",
    lightModeToggle: "تفعيل الوضع الفاتح",
    backToMain: "العودة للرئيسية",
    reset: "إعادة تعيين",
    resetAll: "إعادة تعيين الكل",
  },
  en: {
    title: "Quick Whats",
    description: "Easily generate direct WhatsApp links.",
    enterPhoneNumber: "Enter Phone Number:",
    countryCode: "Country Code:",
    phoneNumberPlaceholder: "e.g., 501234567",
    optionalMessage: "Optional Message (will be pre-filled):",
    messagePlaceholder: "Hello! How can I help you?",
    generateLink: "Generate Link",
    generating: "Generating...",
    openInWhatsApp: "Open in WhatsApp",
    copyLink: "Copy Link",
    copied: "Copied!",
    linkCopiedToClipboard: "Link copied to clipboard!",
    errorInvalidPhoneNumber: "Please enter a valid phone number.",
    errorCountryCodeMissing: "Please select a country code.",
    generatedLink: "Generated Link:",
    uiLanguageToggle: "التبديل إلى العربية (واجهة المستخدم)",
    darkModeToggle: "Switch to dark mode",
    lightModeToggle: "Switch to light mode",
    backToMain: "Back to Main",
    reset: "Reset",
    resetAll: "Reset All",
  }
};

// Common Country Codes - Add more as needed
const countryCodes = [
  { code: "+966", name: "Saudi Arabia (+966)", flag: "🇸🇦" },
  { code: "+971", name: "UAE (+971)", flag: "🇦🇪" },
  { code: "+1", name: "USA (+1)", flag: "🇺🇸" },
  { code: "+44", name: "UK (+44)", flag: "🇬🇧" },
  { code: "+91", name: "India (+91)", flag: "🇮🇳" },
  { code: "+20", name: "Egypt (+20)", flag: "🇪🇬" },
  { code: "+92", name: "Pakistan (+92)", flag: "🇵🇰" },
  // Add more countries here
];

import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('whatsapp');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('whatsapp');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "واتس مباشر - أسامة الزبيدي | إرسال واتساب مباشر مجاناً | Direct WhatsApp - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "واتس مباشر - أسامة الزبيدي | إرسال واتساب مباشر مجاناً" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/quick-whats" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "واتس مباشر - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "واتس مباشر - أسامة الزبيدي | إرسال واتساب مباشر مجاناً" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "واتس مباشر - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/quick-whats" },
    { name: "alternate", content: "https://oz.sa/quick-whats", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/quick-whats", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/quick-whats", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

export default function QuickWhatsPage() {
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  // Default states for reset
  const defaultCountryCode = "+966";
  const defaultPhoneNumber = "";
  const defaultMessage = "";



  const [uiLang, setUiLang] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pageLang') || 'ar';
    }
    return 'ar';
  });

  const t = quickWhatsTranslations[uiLang as keyof typeof quickWhatsTranslations];

  const [countryCode, setCountryCode] = useState<string>(defaultCountryCode);
  const [phoneNumber, setPhoneNumber] = useState<string>(defaultPhoneNumber);
  const [message, setMessage] = useState<string>(defaultMessage);
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);



  useEffect(() => {
    document.documentElement.lang = uiLang;
    document.documentElement.dir = uiLang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('pageLang', uiLang);
  }, [uiLang]);


  const toggleUiLanguage = () => {
    setUiLang(prev => (prev === 'ar' ? 'en' : 'ar'));
    setError(null);
    setGeneratedLink("");
  };

  const handleReset = () => {
    setCountryCode(defaultCountryCode);
    setPhoneNumber(defaultPhoneNumber);
    setMessage(defaultMessage);
    setGeneratedLink("");
    setError(null);
    setIsCopied(false);
  };

  const handleCopyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    }
  };

  const handleGenerateLink = () => {
    setError(null);
    setGeneratedLink("");
    if (!phoneNumber.trim()) {
      setError(t.errorInvalidPhoneNumber);
      return;
    }
    if (!countryCode) {
        setError(t.errorCountryCodeMissing);
        return;
    }

    // Basic validation: remove non-digits from phone number
    const digitsOnlyPhoneNumber = phoneNumber.replace(/\D/g, '');
    if (!digitsOnlyPhoneNumber) {
        setError(t.errorInvalidPhoneNumber);
        return;
    }

    const fullPhoneNumber = `${countryCode.replace('+', '')}${digitsOnlyPhoneNumber}`;
    let link = `https://wa.me/${fullPhoneNumber}`;
    if (message.trim()) {
      link += `?text=${encodeURIComponent(message.trim())}`;
    }
    setGeneratedLink(link);
  };


  return (
    <div className={`quick-whats-container ${uiLang === 'ar' ? 'rtl' : ''}`} dir={uiLang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header matching age calculator design */}
      <header className="quick-whats-header">
        <div className="quick-whats-header-content">
          {/* Back button */}
          <Link
            to="/"
            className="quick-whats-back-btn"
            aria-label={t.backToMain}
          >
            {uiLang === 'ar' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" />
              </svg>
            )}
          </Link>
          
          {/* App title */}
          <h1 className="quick-whats-title">
            {t.title}
          </h1>
          
          {/* Control buttons */}
          <div className="quick-whats-controls">
            <button
              onClick={toggleUiLanguage}
              className="quick-whats-lang-btn"
              aria-label={t.uiLanguageToggle}
            >
              {uiLang === 'ar' ? 'EN' : 'عربي'}
            </button>
          </div>
        </div>
      </header>
      {/* Main content matching age calculator design */}
      <main className="quick-whats-main">
        {/* Icon and header */}
        <div className="quick-whats-content-header">
          <div className="quick-whats-icon">
            💬
          </div>
          <h1>{t.title}</h1>
          <p>{t.description}</p>
        </div>

        {/* Form matching age calculator design */}
        <div className="quick-whats-form">
          {/* Phone number section */}
          <div className="form-section">
            <label>{t.enterPhoneNumber}</label>
            <div className="phone-input-container">
              <select 
                id="countryCode" 
                value={countryCode} 
                onChange={(e) => { setCountryCode(e.target.value); setGeneratedLink(""); setError(null); }}
                className="country-code-select"
              >
                {countryCodes.map(c => (
                  <option key={c.code} value={c.code}>{`${c.flag} ${c.code}`}</option>
                ))}
              </select>
              <div className="phone-number-input">
                <input 
                  type="tel" 
                  id="phoneNumber" 
                  value={phoneNumber} 
                  onChange={(e) => { 
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setPhoneNumber(val); 
                    setGeneratedLink(""); 
                    setError(null); 
                  }} 
                  placeholder={t.phoneNumberPlaceholder} 
                  inputMode="tel"
                />
                {phoneNumber && (
                  <button
                    onClick={handleReset}
                    className="clear-btn"
                    aria-label={t.reset}
                    title={t.resetAll}
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Message section */}
          <div className="form-section">
            <label htmlFor="message">{t.optionalMessage}</label>
            <textarea 
              id="message" 
              value={message} 
              onChange={(e) => { setMessage(e.target.value); setGeneratedLink(""); }}
              className="message-input"
              placeholder={t.messagePlaceholder}
            />
          </div>
          
          {/* Error message */}
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {/* Generate button */}
          <button 
            onClick={handleGenerateLink} 
            disabled={isSubmitting || !phoneNumber} 
            className="generate-btn"
          >
            <LinkIcon className="w-5 h-5" />
            {isSubmitting ? t.generating : t.generateLink}
          </button>

          {/* Results section matching age calculator design */}
          {generatedLink && !error && (
            <div className="results-section">
              <h2>{t.generatedLink}</h2>
              <div className="generated-link-display">
                <p className="generated-link-text">
                  {generatedLink}
                </p>
              </div>
              
              <div className="action-buttons">
                <button 
                  onClick={handleCopyToClipboard} 
                  className="copy-link-btn"
                >
                  <ClipboardIcon className="w-4 h-4" />
                  {isCopied ? t.copied : t.copyLink}
                </button>
                <a 
                  href={generatedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="open-whatsapp-btn"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  {t.openInWhatsApp}
                </a>
              </div>
              {isCopied && (
                <p className="success-message">
                  {t.linkCopiedToClipboard}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Footer matching age calculator design */}
      <footer className="quick-whats-footer">
        <p>
          &copy; {new Date().getFullYear()} 
          <a 
            href="https://oz.sa"
            target="_blank" 
            rel="noopener noreferrer"
          >
            صنع بكل ❤️ في 🇸🇦
          </a>
        </p>
        <p>
          <a
            href="https://www.snapchat.com/add/zk9c"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="تابعنا على السناب شات"
          >
            تابعنا على السناب شات
          </a>
        </p>
      </footer>
    </div>
  );
} 